import { Check, TicketPercent, X } from "lucide-react";
import type React from "react";
import { type ReactNode, useCallback } from "react";
import { ChatState, useProject } from "@/features/project";

interface ApprovalCardProps {
  messageId: string;
  icon: ReactNode;
  label: string;
  meta: string;
  prompt: string;
  credits: number;
  status: "approved" | "rejected" | "pending";
}

export const ApprovalCard: React.FC<ApprovalCardProps> = ({
  messageId,
  icon,
  label,
  meta,
  prompt,
  credits,
  status,
}) => {
  const { submitChat, setCheckpointApproval, chatState } = useProject();
  const isStreaming = chatState !== ChatState.IDLE;
  const statusIcon = status === "rejected" ? <X size={16} className="text-slate-500" /> : icon;

  const textColor = status === "rejected" ? "text-slate-500" : "text-slate-300";

  const resumeChat = useCallback(
    (approved: boolean) => {
      setCheckpointApproval(messageId, approved);
      submitChat("");
    },
    [setCheckpointApproval, submitChat, messageId],
  );

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3 my-2 max-w-sm">
      <div className="flex items-center gap-2 mb-2">
        {statusIcon}
        <span className={`text-xs font-medium ${textColor}`}>{label}</span>
        {status === "approved" && (
          <span className="flex items-center gap-1 text-xs text-slate-400">
            · <TicketPercent size={14} /> {credits}
          </span>
        )}
        <span className="text-xs text-slate-500 ml-auto">{meta}</span>
      </div>

      <p className={`text-sm line-clamp-3 ${textColor}`}>{prompt}</p>

      {status === "pending" && (
        <div className="flex gap-2 mt-2">
          <button
            onClick={() => resumeChat(true)}
            disabled={isStreaming}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-emerald-600 text-white text-xs font-medium transition-colors"
          >
            <Check size={14} />
            <span>Approve</span>
            <span className="text-white/50">·</span>
            <TicketPercent className="text-white/80" size={14} />
            <span className="text-white/80">{credits}</span>
          </button>
          <button
            onClick={() => resumeChat(false)}
            disabled={isStreaming}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-700 text-slate-300 text-xs font-medium transition-colors"
          >
            <X size={14} />
            <span>Reject</span>
          </button>
        </div>
      )}
    </div>
  );
};
